import { injectable } from 'tsyringe';
import { createInterfaceToken } from '@bmai/shared';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import type { AdoptionBreakdownRow, AdoptionScope, AdoptionScore, AdoptionTrend } from './AdoptionTypes';
import { buildBreakdown, buildScore, buildTrend } from './AdoptionFixture';

/**
 * Data source for the adoption screens. The global date range and cross-page
 * dimensions are applied by the underlying analytics gateway; callers pass the
 * page-scoped assist filters (and, for trends, a grouped scope) on top. Bound to
 * {@link OlapAdoptionGateway}; swap {@link FixtureAdoptionGateway} for offline dev.
 */
export interface AdoptionGateway {
    /** Org-level composite score, drivers, context, and trend series. */
    getScore(filters: AnalyticsFilter[]): Promise<AdoptionScore>;
    /** Scored leaf rows for the drivers grid; grouped/expanded client-side by the group-by pills. */
    getBreakdown(filters: AnalyticsFilter[]): Promise<AdoptionBreakdownRow[]>;
    /** Per-driver headline + weekly series, narrowed by the drill path (empty = whole org). */
    getTrend(filters: AnalyticsFilter[], scopePath: AdoptionScope[]): Promise<AdoptionTrend>;
}

export const IAdoptionGatewayToken = createInterfaceToken<AdoptionGateway>('AdoptionGateway');

/** Deterministic sample gateway — surfaces stable illustrative data with no backend calls. */
@injectable()
export class FixtureAdoptionGateway implements AdoptionGateway {
    getScore(): Promise<AdoptionScore> {
        return Promise.resolve(buildScore());
    }

    getBreakdown(): Promise<AdoptionBreakdownRow[]> {
        return Promise.resolve(buildBreakdown());
    }

    getTrend(_filters: AnalyticsFilter[], scopePath: AdoptionScope[]): Promise<AdoptionTrend> {
        return Promise.resolve(buildTrend(scopePath[scopePath.length - 1]));
    }
}
